/*
 * Curated YouTube videos.
 * Without JS the stage and every thumbnail are plain links to YouTube. With JS
 * the stage becomes the player and the thumbnails feed it, so only one film
 * ever runs and the layout never shifts.
 */
(function () {
  'use strict';

  var API = 'https://www.googleapis.com/youtube/v3/';

  function parseChannel(url) {
    if (!url) return null;
    var clean = url.trim().split('?')[0].replace(/\/$/, '');
    var m;
    if ((m = clean.match(/\/channel\/(UC[\w-]+)/))) return { param: 'id', value: m[1] };
    if ((m = clean.match(/\/@([^/]+)/))) return { param: 'forHandle', value: '@' + m[1] };
    if ((m = clean.match(/\/user\/([^/]+)/))) return { param: 'forUsername', value: m[1] };
    if ((m = clean.match(/^(UC[\w-]+)$/))) return { param: 'id', value: m[1] };
    return null;
  }

  function get(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('YouTube API ' + r.status);
      return r.json();
    });
  }

  function titleOf(el) {
    var typed = (el.dataset.videoTitle || '').trim();
    if (typed) return typed;
    var filled = el.querySelector('[data-needs-title]');
    return filled ? filled.textContent.trim() : '';
  }

  /* Rebuilding the iframe rather than reusing it: a fresh element is the one
     way to be certain the previous film has stopped. */
  function playOnStage(root, source) {
    var stage = root.querySelector('[data-ysel-stage]');
    if (!stage) return;

    var id = source.dataset.videoId;
    if (!id) return;

    var old = stage.querySelector('iframe');
    if (old) old.remove();

    var still = stage.querySelector('[data-ysel-stage-img]');
    var play = stage.querySelector('[data-ysel-stage-play]');
    if (still) still.hidden = true;
    if (play) play.hidden = true;

    var frame = document.createElement('iframe');
    frame.src = 'https://www.youtube-nocookie.com/embed/' + id +
      '?rel=0&modestbranding=1&playsinline=1&autoplay=1';
    frame.title = titleOf(source) || 'YouTube video player';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    stage.appendChild(frame);

    var now = root.querySelector('[data-ysel-now]');
    if (now) {
      var t = now.querySelector('[data-ysel-now-title]');
      var d = now.querySelector('[data-ysel-now-date]');
      if (t) t.textContent = titleOf(source);
      if (d) d.textContent = (source.dataset.videoDate || '').trim();
      now.hidden = false;
    }

    markTiles(root, id, true);
  }

  /* One thumbnail at a time carries the mark. "is-current" means this film is
     the one in the big frame — which is what stops the first thumbnail looking
     like a duplicate of the stage — and "is-playing" adds the moving bars. */
  function markTiles(root, id, playing) {
    var onscreen = root.dataset.onscreenLabel || 'On screen';
    var running = root.dataset.playingLabel || 'Playing';

    root.querySelectorAll('[data-ysel-tile]').forEach(function (tile) {
      var mine = tile.dataset.videoId === id;
      tile.classList.toggle('is-current', mine);
      tile.classList.toggle('is-playing', mine && playing);
      var label = tile.querySelector('[data-chip-label]');
      if (label && mine) label.textContent = playing ? running : onscreen;
    });
  }

  /* Steps one thumbnail per press, and hides its own controls when the row
     already fits. */
  function setupStrip(root) {
    var wrap = root.querySelector('[data-ysel-strip]');
    if (!wrap) return;

    var track = wrap.querySelector('[data-ysel-track]');
    var foot = wrap.querySelector('[data-ysel-foot]');
    var fill = wrap.querySelector('[data-ysel-fill]');
    var prev = wrap.querySelector('[data-ysel-prev]');
    var next = wrap.querySelector('[data-ysel-next]');
    if (!track || !foot) return;

    function step() {
      var first = track.querySelector('.ysel-tile');
      if (!first) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var scrollable = max > 1;

      foot.hidden = !scrollable;
      track.classList.toggle('ysel-strip--static', !scrollable);
      if (!scrollable) return;

      var seen = track.scrollLeft + track.clientWidth;
      if (fill) fill.style.width = Math.min(100, (seen / track.scrollWidth) * 100) + '%';
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    }

    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (document.readyState !== 'complete') window.addEventListener('load', update);

    update();
  }

  /* A row whose title never arrived would otherwise render as a blank line. */
  function backstopTitles(root) {
    root.querySelectorAll('[data-needs-title]').forEach(function (el) {
      if (!el.textContent.trim()) el.textContent = 'Watch on YouTube';
    });
  }

  function fillTitles(root) {
    var pending = Array.prototype.slice.call(root.querySelectorAll('[data-needs-title]'));
    if (!pending.length) return;

    var key = root.dataset.apiKey;
    if (!key) { backstopTitles(root); return; }

    // Each tile carries two captions, so the same id shows up twice.
    var ids = [];
    pending.forEach(function (el) {
      var id = el.dataset.needsTitle;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (!ids.length) return;

    get(API + 'videos?part=snippet&id=' + ids.slice(0, 50).join(',') + '&key=' + key)
      .then(function (data) {
        var byId = {};
        (data.items || []).forEach(function (v) { byId[v.id] = v.snippet; });

        pending.forEach(function (el) {
          var snippet = byId[el.dataset.needsTitle];
          if (!snippet) return;
          el.textContent = snippet.title;
        });

        // The stage carries no title of its own until one is typed or fetched.
        var stage = root.querySelector('[data-ysel-stage]');
        if (stage && !(stage.dataset.videoTitle || '').trim()) {
          var snip = byId[stage.dataset.videoId];
          if (snip) stage.dataset.videoTitle = snip.title;
        }
      })
      .catch(function () { /* Falls through to the backstop. */ })
      .then(function () { backstopTitles(root); });
  }

  function fillChannel(root) {
    var nameEl = root.querySelector('[data-needs-name]');
    var avatars = Array.prototype.slice.call(root.querySelectorAll('[data-needs-avatar]'));
    if (!nameEl && !avatars.length) return;

    var key = root.dataset.apiKey;
    var target = parseChannel(root.dataset.channelUrl);
    if (!key || !target) return;

    get(API + 'channels?part=snippet&' + target.param + '=' + encodeURIComponent(target.value) + '&key=' + key)
      .then(function (data) {
        var snippet = data.items && data.items[0] && data.items[0].snippet;
        if (!snippet) return;

        if (nameEl) nameEl.textContent = snippet.title;

        var thumbs = snippet.thumbnails || {};
        var src = (thumbs.high || thumbs.medium || thumbs.default || {}).url;
        if (!src) return;

        avatars.forEach(function (img) {
          img.src = src;
          img.alt = snippet.title;
          img.removeAttribute('hidden');
        });
      })
      .catch(function () { /* The masthead stays as the merchant left it. */ });
  }

  function guardThumbnails(root) {
    root.querySelectorAll('img[data-fallback]').forEach(function (img) {
      img.addEventListener('error', function handler() {
        img.removeEventListener('error', handler);
        img.src = img.dataset.fallback;
      });
    });
  }

  function init(root) {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    var stage = root.querySelector('[data-ysel-stage]');
    if (stage) {
      stage.addEventListener('click', function (event) {
        event.preventDefault();
        playOnStage(root, stage);
      });
      markTiles(root, stage.dataset.videoId, false);
    }

    root.querySelectorAll('[data-ysel-tile]').forEach(function (tile) {
      tile.addEventListener('click', function (event) {
        event.preventDefault();
        playOnStage(root, tile);
      });
    });

    setupStrip(root);
    guardThumbnails(root);

    if (root.dataset.autofill === 'true') {
      fillTitles(root);
      fillChannel(root);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ysel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
